import { useEffect, useId, useMemo } from 'react';
import LatencySparkline from '../components/LatencySparkline.js';
import LatencyTrendChart, {
  RegionLatencyComparisonChart,
} from '../components/LatencyTrendChart.js';
import CommandPanel from '../components/ui/CommandPanel.js';
import HelpTooltip from '../components/ui/HelpTooltip.js';
import MetricTile from '../components/ui/MetricTile.js';
import SectionHeader from '../components/ui/SectionHeader.js';
import StatusGlow from '../components/ui/StatusGlow.js';
import { useLatencyFeed } from '../hooks/useLatencyFeed.js';
import { useLatencyHealth } from '../hooks/useLatencyHealth.js';
import { useLatencyHistory } from '../hooks/useLatencyHistory.js';
import { usePersistedPanelState } from '../hooks/usePersistedPanelState.js';
import { usePersistedSetting } from '../hooks/usePersistedSetting.js';
import type { LatencyAggRow, LatencyHistoryRow } from '../lib/api.js';

const DEFAULT_WINDOWS = ['1', '6', '24', '72', '168'];
const MAX_TARGETS_SHOWN = 15;
const HISTORY_TITLES: Record<string, string> = {
  '1': 'Last hour',
  '6': 'Last 6 hours',
  '24': 'Last 24 hours',
  '72': 'Last 72 hours',
  '168': 'Last 7 days',
};
const MAJ_THRESHOLD_MS = 900;
const DOWN_THRESHOLD_MS = 1500;
const LOW_SAMPLE_THRESHOLD = 3;
const SAMPLE_WARNING_THRESHOLD = 2;
const D1_STALE_MINUTES = 45;
const TARGET_BAR_LIMIT = 10;
const REGION_DESCRIPTIONS: Record<string, string> = {
  WNAM: 'Western North America',
  ENAM: 'Eastern North America',
  WEUR: 'Western Europe',
  EEUR: 'Eastern Europe',
  APAC: 'Asia-Pacific',
  OC: 'Oceania',
  SAM: 'South America',
  AFR: 'Africa',
  ME: 'Middle East',
};
const REGION_DISPLAY_ORDER = ['ENAM', 'WNAM', 'WEUR', 'EEUR', 'SAM', 'OC', 'APAC', 'AFR', 'ME'];
const REGION_ORDER_INDEX = new Map(REGION_DISPLAY_ORDER.map((region, index) => [region, index]));

type LatencyStatusTone = 0 | 1 | 2 | 3 | 'missing';

interface LatencyStatusDistribution {
  ok: number;
  deg: number;
  maj: number;
  down: number;
  total: number;
}

const LATENCY_STATUS_LABELS: Record<number, string> = {
  0: 'OK',
  1: 'DEG',
  2: 'MAJ',
  3: 'DOWN',
};
const LATENCY_STATUS_LABELS_MISSING = '—';
const LATENCY_STATUS_TONES: Record<LatencyStatusTone, string> = {
  0: 'OK',
  1: 'DEG',
  2: 'MAJ',
  3: 'DOWN',
  missing: LATENCY_STATUS_LABELS_MISSING,
};

function severityTone(row?: LatencyAggRow | null): LatencyStatusTone {
  if (!row) return 'missing';
  if (!Number.isFinite(row.samples) || row.samples <= 0) return 'missing';
  return classifyLatencySeverity(row);
}

function severityLabel(tone: LatencyStatusTone): string {
  return LATENCY_STATUS_TONES[tone];
}

function severityToneClass(tone: LatencyStatusTone): string {
  if (tone === 2) {
    return 'bg-bad/20 border-l-2 border-bad/50';
  }
  if (tone === 'missing') return 'bg-surface/20';
  if (tone === 3) return 'bg-surface/20';
  if (tone === 1) return 'bg-warn/10';
  return '';
}

function latencyProfileBarClass(tone: LatencyStatusTone, metric: 'p50' | 'p95'): string {
  if (tone === 0) return metric === 'p50' ? 'bg-ok/70' : 'bg-ok/25';
  if (tone === 1) return metric === 'p50' ? 'bg-warn/80' : 'bg-warn/25';
  if (tone === 2) return metric === 'p50' ? 'bg-orange-400/75' : 'bg-orange-400/25';
  if (tone === 3) return metric === 'p50' ? 'bg-bad/75' : 'bg-bad/25';
  return metric === 'p50' ? 'bg-dim/70' : 'bg-dim/25';
}

function historySeries(rows: LatencyHistoryRow[], target: string): number[] {
  return rows.filter((row) => row.target === target).map((row) => row.p95_ms);
}

function classifyLatencySeverity(row: LatencyAggRow): 0 | 1 | 2 | 3 {
  if (
    !Number.isFinite(row.samples) ||
    row.samples <= 0 ||
    !Number.isFinite(row.p95_ms) ||
    row.p95_ms <= 0
  ) {
    return 3;
  }
  if (row.p95_ms >= DOWN_THRESHOLD_MS) return 3;
  if (row.p95_ms >= MAJ_THRESHOLD_MS) return 2;
  if (row.p95_ms >= 300) return 1;
  return 0;
}

function formatMs(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${Math.round(value)}ms`;
}

function describeRegion(region: string): string {
  return REGION_DESCRIPTIONS[region] ?? region;
}

function formatSamples(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  if (value >= 1000) return `${Math.round(value / 1000)}k`;
  return `${Math.round(value)}`;
}

function formatPercent(part: number, total: number): string {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return '0%';
  return `${Math.max(0, Math.min(100, Math.round((part / total) * 100)))}%`;
}

function summarizeSeverity(rows: LatencyAggRow[]): LatencyStatusDistribution {
  const counts = rows.reduce(
    (acc, row) => {
      const severity = classifyLatencySeverity(row);
      if (severity === 0) acc.ok += 1;
      else if (severity === 1) acc.deg += 1;
      else if (severity === 2) acc.maj += 1;
      else acc.down += 1;
      return acc;
    },
    { ok: 0, deg: 0, maj: 0, down: 0, total: 0 },
  );
  counts.total = rows.length;
  return counts;
}

function formatSampleWindow(rows: LatencyHistoryRow[]): string {
  if (rows.length === 0) return 'No samples';
  const latest = rows[rows.length - 1];
  if (!latest) return 'No samples';
  const seen = new Set(rows.map((r) => r.ts));
  return `${rows.length} records · ${seen.size} snapshots · latest ${new Date(latest.ts).toLocaleTimeString()}`;
}

function deriveLatestByTarget(rows: LatencyAggRow[]): Record<string, LatencyAggRow> {
  const out: Record<string, LatencyAggRow> = {};
  for (const row of rows) {
    out[row.target] = row;
  }
  return out;
}

function deriveLatestHistoryByTarget(rows: LatencyHistoryRow[]): Record<string, LatencyHistoryRow> {
  const ordered = [...rows].sort((a, b) => (a.ts === b.ts ? 0 : a.ts < b.ts ? -1 : 1));
  const out: Record<string, LatencyHistoryRow> = {};
  for (const row of ordered) {
    out[row.target] = row;
  }
  return out;
}

function weightedQuantile(
  rows: LatencyHistoryRow[],
  metric: 'p50_ms' | 'p95_ms',
  quantile: number,
): number {
  if (rows.length === 0) return 0;
  const ordered = [...rows]
    .filter(
      (row) => Number.isFinite(row[metric]) && Number.isFinite(row.samples) && row.samples > 0,
    )
    .sort((a, b) => a[metric] - b[metric]);
  const totalWeight = ordered.reduce((sum, row) => sum + row.samples, 0);
  if (ordered.length === 0 || totalWeight <= 0) return 0;
  const threshold = totalWeight * quantile;
  let cumulative = 0;
  for (const row of ordered) {
    cumulative += row.samples;
    if (cumulative >= threshold) return row[metric];
  }
  return ordered[ordered.length - 1]?.[metric] ?? 0;
}

export function aggregateHistoryByTarget(rows: LatencyHistoryRow[]): Record<string, LatencyAggRow> {
  const grouped = new Map<string, LatencyHistoryRow[]>();
  for (const row of rows) {
    const bucket = grouped.get(row.target);
    if (bucket) bucket.push(row);
    else grouped.set(row.target, [row]);
  }

  const out: Record<string, LatencyAggRow> = {};
  for (const [target, targetRows] of grouped) {
    const first = targetRows[0];
    if (!first) continue;
    out[target] = {
      region: first.region,
      target,
      p50_ms: weightedQuantile(targetRows, 'p50_ms', 0.5),
      p95_ms: weightedQuantile(targetRows, 'p95_ms', 0.95),
      samples: targetRows.reduce((sum, row) => sum + row.samples, 0),
    };
  }
  return out;
}

interface RegionSummary {
  region: string;
  targets: number;
  severity: 0 | 1 | 2 | 3;
  samples: number;
  avgP50: number;
  avgP95: number;
}

interface LatencyTargetRow {
  target: string;
  p50_ms: number;
  p95_ms: number;
  samples: number;
  tone: LatencyStatusTone;
}

interface LatencyTableRow {
  target: string;
  row?: LatencyAggRow;
  latest?: LatencyHistoryRow;
  samples: number;
  severity: LatencyStatusTone;
  trendDirection: 'up' | 'down' | 'flat';
  trendMs: number;
  hasHistory: boolean;
}

export function buildLatencyTableRows(
  targets: string[],
  regionHistoryRows: LatencyHistoryRow[],
  displayByTarget: Record<string, LatencyAggRow>,
): LatencyTableRow[] {
  return targets.map((target) => {
    const row = displayByTarget[target];
    const trendRows = regionHistoryRows.filter((r) => r.target === target);
    const latest = trendRows.at(-1);
    const previous = trendRows[trendRows.length - 2];
    const deltaMs = latest && previous ? latest.p50_ms - previous.p50_ms : 0;
    const trendDirection =
      latest && previous ? (deltaMs > 5 ? 'up' : deltaMs < -5 ? 'down' : 'flat') : 'flat';
    const baseRow = row ?? latest;
    const severity = severityTone(baseRow);
    return {
      target,
      row: baseRow,
      latest,
      samples: baseRow?.samples ?? 0,
      severity,
      trendDirection,
      trendMs: Math.abs(deltaMs),
      hasHistory: trendRows.length > 0,
    };
  });
}

interface LatencySourceHealth {
  selectedRows: number;
  selectedRecentTargets: number;
  selectedHistoryRows: number;
  selectedHistoryTargets: number;
  snapshotLikeRows: number;
  parityRows: number;
  multiSampleRows: number;
  sourceMode: 'D1 history' | 'hybrid snapshot' | 'cache snapshot';
  latestHistoryTs: string | null;
  historyAgeMinutes: number | null;
  historyStale: boolean;
  lowSampleHistoryRows: number;
  parityHistoryRows: number;
  multiSampleHistoryRows: number;
}

interface D1HistoryHealth {
  hasRows: boolean;
  totalRows: number;
  targetCount: number;
  regionCount: number;
  snapshotCount: number;
  latestTargets: number;
  multiSampleTargets: number;
  parityTargets: number;
  spreadTargets: number;
  latestTs: string | null;
  latestAgeMinutes: number | null;
  isStale: boolean;
}

function buildD1HistoryHealth(rows: LatencyHistoryRow[], region?: string): D1HistoryHealth {
  const scopedRows = region ? rows.filter((row) => row.region === region) : rows;
  const totalRows = scopedRows.length;
  if (totalRows === 0) {
    return {
      hasRows: false,
      totalRows: 0,
      targetCount: 0,
      regionCount: 0,
      snapshotCount: 0,
      latestTargets: 0,
      multiSampleTargets: 0,
      parityTargets: 0,
      spreadTargets: 0,
      latestTs: null,
      latestAgeMinutes: null,
      isStale: false,
    };
  }

  const latestTs = scopedRows.reduce<string | null>((latest, row) => {
    if (!latest) return row.ts;
    return row.ts > latest ? row.ts : latest;
  }, null);
  const latestTargets = scopedRows.filter((row) => row.ts === latestTs);
  const targetSet = new Set(scopedRows.map((row) => `${row.region}:${row.target}`));
  const regionSet = new Set(scopedRows.map((row) => row.region));
  const snapshotSet = new Set(scopedRows.map((row) => row.ts));
  const spreadTargets = latestTargets.filter((row) => row.p95_ms > row.p50_ms).length;
  const parityTargets = latestTargets.filter((row) => row.p50_ms === row.p95_ms).length;
  const multiSampleTargets = latestTargets.filter((row) => row.samples > 1).length;
  let latestAgeMinutes: number | null = null;
  let isStale = false;
  if (latestTs) {
    const age = (Date.now() - Date.parse(latestTs)) / (60 * 1000);
    latestAgeMinutes = Number.isFinite(age) ? age : null;
    isStale = latestAgeMinutes !== null && latestAgeMinutes > D1_STALE_MINUTES;
  }

  return {
    hasRows: true,
    totalRows,
    targetCount: targetSet.size,
    regionCount: regionSet.size,
    snapshotCount: snapshotSet.size,
    latestTargets: latestTargets.length,
    multiSampleTargets,
    parityTargets,
    spreadTargets,
    latestTs,
    latestAgeMinutes,
    isStale,
  };
}

function isLikelyProbeSnapshotRows(rows: LatencyAggRow[]): boolean {
  return rows.length > 0 && rows.every((row) => row.samples <= 1 && row.p50_ms === row.p95_ms);
}

function summarizeQuality(rows: LatencyAggRow[], historyRows: LatencyHistoryRow[]): string | null {
  const hasRows = rows.length > 0 || historyRows.length > 0;
  if (!hasRows) {
    return 'No latency aggregates are available yet. Check that latency cron and D1 writes are running.';
  }
  if (historyRows.length === 0 && isLikelyProbeSnapshotRows(rows)) {
    return (
      'This snapshot looks like per-target one-sample checks (' +
      'p50 ≈ p95 and sample count is low). D1 historical aggregates should replace this ' +
      'once the 15-minute cron has written history rows.'
    );
  }
  const hasVariance =
    rows.some((row) => row.p95_ms !== row.p50_ms) && rows.some((row) => row.samples > 1);
  if (!hasVariance && rows.length > 0) {
    return (
      'Low variance in this window: all currently visible targets have equal p50 and p95. ' +
      'This can happen on very stable routes, but verify for sustained flat lines.'
    );
  }

  const latestHistoryTs = historyRows.reduce<string | null>((latest, row) => {
    if (!latest) return row.ts;
    return row.ts > latest ? row.ts : latest;
  }, null);
  if (latestHistoryTs) {
    const ageMin = (Date.now() - Date.parse(latestHistoryTs)) / (60 * 1000);
    if (Number.isFinite(ageMin) && ageMin > 180) {
      return 'Warning: D1 history is stale. Latest snapshot is older than 3h; verify the scheduled latency job and database writes.';
    }
  }
  return null;
}

function buildSourceHealth(
  selectedRegion: string,
  recentRows: LatencyAggRow[],
  historyRows: LatencyHistoryRow[],
): LatencySourceHealth {
  const selectedRecentRows = recentRows.filter((row) => row.region === selectedRegion);
  const selectedRecentTargets = new Set(selectedRecentRows.map((row) => row.target)).size;
  const selectedHistoryTargets = new Set(historyRows.map((row) => row.target)).size;
  const snapshotLikeRows = selectedRecentRows.filter(
    (row) => row.samples <= 1 && row.p50_ms === row.p95_ms,
  ).length;
  const parityRows = selectedRecentRows.filter((row) => row.p50_ms === row.p95_ms).length;
  const multiSampleRows = selectedRecentRows.filter(
    (row) => row.samples > SAMPLE_WARNING_THRESHOLD,
  ).length;
  const lowSampleHistoryRows = historyRows.filter(
    (row) => row.samples <= LOW_SAMPLE_THRESHOLD,
  ).length;
  const parityHistoryRows = historyRows.filter((row) => row.p50_ms === row.p95_ms).length;
  const multiSampleHistoryRows = historyRows.filter((row) => row.samples > 1).length;
  const latestHistoryTs =
    historyRows.length > 0 ? (historyRows[historyRows.length - 1]?.ts ?? null) : null;
  let historyAgeMinutes: number | null = null;
  let historyStale = false;
  if (historyRows.length > 0 && latestHistoryTs) {
    const age = (Date.now() - Date.parse(latestHistoryTs)) / (60 * 1000);
    historyAgeMinutes = Number.isFinite(age) ? age : null;
    historyStale = historyAgeMinutes !== null && historyAgeMinutes > 45;
  }

  const sourceMode: LatencySourceHealth['sourceMode'] =
    historyRows.length > 0
      ? 'D1 history'
      : snapshotLikeRows === selectedRecentRows.length && selectedRecentRows.length > 0
        ? 'cache snapshot'
        : 'hybrid snapshot';

  return {
    selectedRows: selectedRecentRows.length,
    selectedRecentTargets,
    selectedHistoryRows: historyRows.length,
    selectedHistoryTargets,
    snapshotLikeRows,
    parityRows,
    multiSampleRows,
    lowSampleHistoryRows,
    parityHistoryRows,
    multiSampleHistoryRows,
    sourceMode,
    latestHistoryTs,
    historyAgeMinutes,
    historyStale,
  };
}

export default function LatencyProbesPage() {
  const [tabExpanded, setTabExpanded] = usePersistedPanelState('panel:latency:overview', false);
  const [methodExpanded, setMethodExpanded] = usePersistedPanelState('panel:latency:method', true);
  const [d1HealthExpanded, setD1HealthExpanded] = usePersistedPanelState(
    'panel:latency:d1-health',
    true,
  );
  const [pipelineExpanded, setPipelineExpanded] = usePersistedPanelState(
    'panel:latency:pipeline',
    true,
  );
  const [tableExpanded, setTableExpanded] = usePersistedPanelState('panel:latency:table', true);
  const [chartExpanded, setChartExpanded] = usePersistedPanelState('panel:latency:chart', true);
  const [allRegionsChartExpanded, setAllRegionsChartExpanded] = usePersistedPanelState(
    'panel:latency:all-regions-chart',
    true,
  );
  const [rankingExpanded, setRankingExpanded] = usePersistedPanelState(
    'panel:latency:ranking',
    true,
  );
  const [regionExpanded, setRegionExpanded] = usePersistedPanelState('panel:latency:regions', true);
  const [metricsExpanded, setMetricsExpanded] = usePersistedPanelState(
    'panel:latency:metrics',
    true,
  );
  const [selectedRegion, setSelectedRegion] = usePersistedSetting('latency:region', 'ENAM');
  const [historyHours, setHistoryHours] = usePersistedSetting('latency:history-hours', '24');
  const { latency: latencyResource } = useLatencyFeed({ pollIntervalMs: 60_000 });
  const historyHoursValue = Number(historyHours) || 24;
  const overviewId = useId();
  const methodId = useId();
  const metricsId = useId();
  const regionId = useId();
  const tableId = useId();
  const chartId = useId();
  const allRegionsChartId = useId();
  const rankingId = useId();
  const pipelineId = useId();
  const d1HealthId = useId();
  const regions = useMemo(() => {
    const uniq = new Set<string>();
    for (const row of latencyResource.data ?? []) {
      uniq.add(row.region);
    }
    return Array.from(uniq).sort((a, b) => {
      const aIndex = REGION_ORDER_INDEX.get(a) ?? Number.MAX_SAFE_INTEGER;
      const bIndex = REGION_ORDER_INDEX.get(b) ?? Number.MAX_SAFE_INTEGER;
      return aIndex === bIndex ? a.localeCompare(b) : aIndex - bIndex;
    });
  }, [latencyResource.data]);

  const selected = regions.includes(selectedRegion) ? selectedRegion : (regions[0] ?? 'ENAM');
  const { history: historyResource } = useLatencyHistory({
    region: selected,
    hours: historyHoursValue,
    pollIntervalMs: 60_000,
  });
  const { history: allHistoryResource } = useLatencyHistory({
    hours: historyHoursValue,
    pollIntervalMs: 60_000,
  });
  const { health: healthResource } = useLatencyHealth({
    region: selected,
    hours: historyHoursValue,
    pollIntervalMs: 60_000,
  });

  useEffect(() => {
    if (selected !== selectedRegion) {
      setSelectedRegion(selected);
    }
  }, [selected, selectedRegion, setSelectedRegion]);

  const historyRows = historyResource.data ?? [];
  const allHistoryRows = allHistoryResource.data ?? [];
  const regionHistoryRows = historyRows.filter((row) => row.region === selected);
  const regionRows = (latencyResource.data ?? []).filter((row) => row.region === selected);
  const allHistoryHealth = useMemo(() => buildD1HistoryHealth(allHistoryRows), [allHistoryRows]);
  const selectedHistoryHealth = useMemo(
    () => buildD1HistoryHealth(allHistoryRows, selected),
    [allHistoryRows, selected],
  );
  const sourceHealth = useMemo(
    () => buildSourceHealth(selected, latencyResource.data ?? [], regionHistoryRows),
    [selected, latencyResource.data, regionHistoryRows],
  );
  const latestByTarget = useMemo(() => deriveLatestByTarget(regionRows), [regionRows]);
  const latestByHistoryTarget = useMemo(
    () => deriveLatestHistoryByTarget(regionHistoryRows),
    [regionHistoryRows],
  );
  const windowAggregateByTarget = useMemo(
    () => aggregateHistoryByTarget(regionHistoryRows),
    [regionHistoryRows],
  );
  const displayByTarget = regionHistoryRows.length > 0 ? windowAggregateByTarget : latestByTarget;
  const hasHistory = regionHistoryRows.length > 0;
  const dataSource =
    hasHistory || regionRows.length === 0
      ? 'D1 history window aggregate'
      : 'latest cache snapshot (single-run probes)';
  const targets = useMemo(() => {
    const set = new Set<string>();
    for (const row of regionRows) set.add(row.target);
    for (const row of regionHistoryRows) set.add(row.target);
    return Array.from(set).sort();
  }, [regionRows, regionHistoryRows]);

  const regionSummaries = useMemo(() => {
    const allRows = latencyResource.data ?? [];
    return regions.map((region) => {
      const rows = deriveLatestByTarget(allRows.filter((row) => row.region === region));
      const regionTargetRows = Object.values(rows);
      const targetCount = regionTargetRows.length;
      const samples = regionTargetRows.reduce((total, row) => total + row.samples, 0);
      const avgP50 =
        targetCount === 0
          ? 0
          : regionTargetRows.reduce((sum, row) => sum + row.p50_ms, 0) / targetCount;
      const avgP95 =
        targetCount === 0
          ? 0
          : regionTargetRows.reduce((sum, row) => sum + row.p95_ms, 0) / targetCount;
      const severity = regionTargetRows.reduce<0 | 1 | 2 | 3>((max, row) => {
        const level = classifyLatencySeverity(row);
        return level > max ? level : max;
      }, 0);
      return {
        region,
        targets: targetCount,
        severity,
        samples: Math.round(samples),
        avgP50,
        avgP95,
      };
    }) satisfies RegionSummary[];
  }, [regions, latencyResource.data]);

  const selectedRows = Object.values(displayByTarget);
  const tableRows = buildLatencyTableRows(targets, regionHistoryRows, displayByTarget);

  const targetColumns = targets.slice(0, MAX_TARGETS_SHOWN);
  const selectedRowsDisplay = useMemo(
    () =>
      targetColumns
        .map((target) => displayByTarget[target])
        .filter((row): row is LatencyAggRow => Boolean(row)),
    [targetColumns, displayByTarget],
  );
  const avgSamples = selectedRowsDisplay.length
    ? selectedRowsDisplay.reduce((total, row) => total + row.samples, 0) /
      selectedRowsDisplay.length
    : 0;
  const avgP50 = selectedRowsDisplay.length
    ? selectedRowsDisplay.reduce((total, row) => total + row.p50_ms, 0) / selectedRowsDisplay.length
    : 0;
  const avgP95 = selectedRowsDisplay.length
    ? selectedRowsDisplay.reduce((total, row) => total + row.p95_ms, 0) / selectedRowsDisplay.length
    : 0;
  const latestHistoryRows = Object.values(latestByHistoryTarget);
  const latestHistoryTargetsMultiSampleCount = latestHistoryRows.filter(
    (row) => row.samples > 1,
  ).length;
  const latestHistoryTargetSpreadCount = latestHistoryRows.filter(
    (row) => row.p95_ms > row.p50_ms,
  ).length;
  const hasHistoryRows = latestHistoryRows.length > 0;
  const maxP95 = selectedRowsDisplay.reduce((max, row) => Math.max(max, row.p95_ms), 0);
  const majorCount = selectedRowsDisplay.reduce(
    (count, row) => (classifyLatencySeverity(row) === 2 ? count + 1 : count),
    0,
  );
  const downCount = selectedRowsDisplay.reduce(
    (count, row) => (classifyLatencySeverity(row) === 3 ? count + 1 : count),
    0,
  );
  const severityDist = useMemo(() => summarizeSeverity(selectedRowsDisplay), [selectedRowsDisplay]);
  const severityBars = useMemo(
    () => [
      {
        label: 'OK',
        level: 0,
        count: severityDist.ok,
        tone: 'bg-ok',
      },
      {
        label: 'DEG',
        level: 1,
        count: severityDist.deg,
        tone: 'bg-warn',
      },
      {
        label: 'MAJ',
        level: 2,
        count: severityDist.maj,
        tone: 'bg-orange-400',
      },
      {
        label: 'DOWN',
        level: 3,
        count: severityDist.down,
        tone: 'bg-bad',
      },
    ],
    [severityDist],
  );
  const maxBarCount = Math.max(1, ...severityBars.map((bar) => bar.count));
  const severitySummary = `${severityDist.ok} OK · ${severityDist.deg} DEG · ${severityDist.maj} MAJ · ${severityDist.down} DOWN`;
  const severityNote =
    downCount > 0
      ? `DOWN values have p95 above ${DOWN_THRESHOLD_MS}ms in ${selected} in this snapshot.`
      : null;
  const qualityWarning = summarizeQuality(selectedRowsDisplay, regionHistoryRows);
  const allHistoryCoverageRate =
    sourceHealth.selectedRows > 0 && selectedHistoryHealth.targetCount > 0
      ? formatPercent(selectedHistoryHealth.targetCount, sourceHealth.selectedRows)
      : '0%';
  const selectedHistoryParityRate = formatPercent(
    selectedHistoryHealth.parityTargets,
    selectedHistoryHealth.latestTargets,
  );
  const selectedHistoryMultiSampleRate = formatPercent(
    selectedHistoryHealth.multiSampleTargets,
    selectedHistoryHealth.latestTargets,
  );
  const selectedHistorySpreadRate = formatPercent(
    selectedHistoryHealth.spreadTargets,
    selectedHistoryHealth.latestTargets,
  );
  const d1HealthRows = healthResource.data?.rowCount ?? selectedHistoryHealth.totalRows;
  const d1HealthTargets = healthResource.data?.targetCount ?? selectedHistoryHealth.targetCount;
  const d1HealthRegions = healthResource.data?.regionCount ?? selectedHistoryHealth.regionCount;
  const d1HealthSnapshots =
    healthResource.data?.snapshotCount ?? selectedHistoryHealth.latestTargets;
  const d1HealthLatestSnapshotTargets =
    healthResource.data?.latestSnapshotTargets ?? selectedHistoryHealth.latestTargets;
  const d1HealthLatestTargetsMultiSample =
    healthResource.data?.latestSnapshotMultiSampleRows ?? selectedHistoryHealth.multiSampleTargets;
  const d1HealthLatestTargetsParity =
    healthResource.data?.latestSnapshotParityRows ?? selectedHistoryHealth.parityTargets;
  const d1HealthLatestTargetsSpread =
    healthResource.data?.latestSnapshotSpreadRows ?? selectedHistoryHealth.spreadTargets;
  const d1HealthLatestTs = healthResource.data?.latestTs ?? selectedHistoryHealth.latestTs;
  const d1HealthLatestAgeMinutes = healthResource.data?.latestSnapshotAgeMinutes;
  const d1HealthSampleMin = healthResource.data?.latestSnapshotSampleMin;
  const d1HealthSampleMax = healthResource.data?.latestSnapshotSampleMax;
  const d1HealthSampleAvg = healthResource.data?.latestSnapshotSampleAvg;
  const d1HealthSampleParityRate =
    d1HealthLatestSnapshotTargets > 0
      ? formatPercent(d1HealthLatestTargetsParity, d1HealthLatestSnapshotTargets)
      : '0%';
  const d1HealthMultiSampleRate =
    d1HealthLatestSnapshotTargets > 0
      ? formatPercent(d1HealthLatestTargetsMultiSample, d1HealthLatestSnapshotTargets)
      : '0%';
  const d1HealthSpreadRate =
    d1HealthLatestSnapshotTargets > 0
      ? formatPercent(d1HealthLatestTargetsSpread, d1HealthLatestSnapshotTargets)
      : '0%';
  const d1HealthStatusText = healthResource.error
    ? 'unavailable'
    : !healthResource.ready
      ? healthResource.loading
        ? 'loading health...'
        : 'waiting'
      : healthResource.data?.isStale
        ? 'stale'
        : 'verified';
  const d1HealthMessage = useMemo(() => {
    const healthFromApi = healthResource.data;
    if (healthFromApi) {
      if (!healthFromApi.rowCount) {
        return 'No D1 aggregate rows returned for this window. Live rows may be cache-only.';
      }
      if (healthFromApi.isStale) {
        return 'D1 aggregates are stale in this window. Verify the api-worker scheduled latency job and AE dataset writes.';
      }
      if (
        healthFromApi.latestSnapshotTargets > 0 &&
        healthFromApi.latestSnapshotMultiSampleRows === 0
      ) {
        return `Latest D1 snapshot for ${selected} has only one-sample rows (${Math.round(
          (healthFromApi.latestSnapshotMultiSampleRows / healthFromApi.latestSnapshotTargets) * 100,
        )}% multi-sample). If this persists, check that the 15-min aggregate cron is running.`;
      }
      return 'D1 history shows multi-sample rows in the latest snapshot.';
    }
    if (!selectedHistoryHealth.hasRows) {
      if (allHistoryRows.length > 0) {
        return 'This region has no D1 rows in the selected window; historical probe aggregates are likely still behind.';
      }
      return 'No D1 history was returned yet in this window. Snapshot data is likely live cache only.';
    }
    if (selectedHistoryHealth.isStale) {
      return 'D1 aggregates are stale in this window. Verify the api-worker scheduled latency job and AE dataset writes.';
    }
    if (selectedHistoryHealth.latestTargets > 0 && selectedHistoryHealth.multiSampleTargets === 0) {
      return `Latest D1 snapshot for ${selected} has only one-sample rows (${selectedHistoryMultiSampleRate} multi-sample). If this persists, check that the 15-min aggregate cron is running.`;
    }
    return 'D1 history shows multi-sample rows in the latest snapshot.';
  }, [
    allHistoryRows.length,
    healthResource.data,
    selectedHistoryHealth,
    selected,
    selectedHistoryMultiSampleRate,
  ]);
  const parityRate = formatPercent(
    selectedRowsDisplay.filter((row) => row.p50_ms === row.p95_ms).length,
    selectedRowsDisplay.length,
  );
  const multiSampleRate = formatPercent(
    selectedRowsDisplay.filter((row) => row.samples > 1).length,
    selectedRowsDisplay.length,
  );
  const historicalTrendCount = regionHistoryRows.length;
  const hasHistoryCoverage = sourceHealth.selectedRecentTargets > 0;
  const historyCoverageRate = hasHistoryCoverage
    ? formatPercent(sourceHealth.selectedHistoryTargets, sourceHealth.selectedRecentTargets)
    : '0%';
  const historyP95SpreadRate =
    selectedRowsDisplay.length > 0
      ? formatPercent(
          selectedRowsDisplay.filter((row) => row.p95_ms > row.p50_ms).length,
          selectedRowsDisplay.length,
        )
      : '0%';
  const sourceModeTone = sourceHealth.sourceMode === 'D1 history' ? 'ok' : 'warn';
  const latestHistorySummary = hasHistoryRows
    ? `${latestHistoryTargetsMultiSampleCount}/${latestHistoryRows.length} targets with more than one sample · ${latestHistoryTargetSpreadCount} with p95 spread`
    : 'no recent D1 aggregates yet';
  const d1HistorySampleRange =
    typeof d1HealthSampleMin === 'number' &&
    typeof d1HealthSampleMax === 'number' &&
    typeof d1HealthSampleAvg === 'number'
      ? `${formatSamples(d1HealthSampleMin)} - ${formatSamples(d1HealthSampleMax)} (avg ${formatSamples(d1HealthSampleAvg)})`
      : 'n/a';
  const topTargetRows = useMemo(
    () =>
      selectedRowsDisplay
        .filter((row) => Number.isFinite(row.p95_ms) && row.samples > 0)
        .sort((a, b) => b.p95_ms - a.p95_ms)
        .slice(0, TARGET_BAR_LIMIT)
        .map(
          (row) =>
            ({
              target: row.target,
              p50_ms: row.p50_ms,
              p95_ms: row.p95_ms,
              samples: row.samples,
              tone: severityTone(row),
            }) satisfies LatencyTargetRow,
        ),
    [selectedRowsDisplay],
  );
  const topBarP95Max = topTargetRows.reduce((max, row) => Math.max(max, row.p95_ms), 0);

  return (
    <div className="min-h-screen bg-bg text-text">
      <main className="max-w-[1400px] mx-auto p-4 grid gap-4">
        <CommandPanel>
          <SectionHeader
            title={
              <span className="flex items-center gap-2">
                <span>LATENCY OVERVIEW</span>
                <HelpTooltip content="Each row uses the latest available aggregate for the selected region." />
              </span>
            }
            expanded={tabExpanded}
            onToggle={() => setTabExpanded((v) => !v)}
            contentId={overviewId}
            right={
              <span className="text-dim normal-case tracking-normal font-normal">
                {selected} · {formatSampleWindow(historyRows)}
              </span>
            }
          />
          {tabExpanded && (
            <div id={overviewId} className="px-3 pb-3 pt-1 space-y-3 text-xs leading-relaxed">
              <div className="grid md:grid-cols-4 gap-2">
                <MetricTile
                  label="Targets in scope"
                  value={String(selectedRows.length)}
                  details={`${regionHistoryRows.length > 0 ? 'history-driven' : 'snapshot-driven'} data`}
                  tone="default"
                />
                <MetricTile
                  label="Avg p50"
                  value={formatMs(avgP50)}
                  details={
                    selectedRows.length ? `${targets.length} visible targets` : 'waiting for sample'
                  }
                  tone={
                    avgP95 >= MAJ_THRESHOLD_MS
                      ? 'bad'
                      : avgP95 >= 300
                        ? 'warn'
                        : avgP50 > 0
                          ? 'ok'
                          : 'default'
                  }
                />
                <MetricTile
                  label="Avg p95"
                  value={formatMs(avgP95)}
                  details={`max ${formatMs(maxP95)}`}
                  tone={avgP95 >= MAJ_THRESHOLD_MS ? 'bad' : avgP95 > 0 ? 'warn' : 'default'}
                />
                <MetricTile
                  label="Avg samples"
                  value={formatSamples(avgSamples)}
                  details={dataSource}
                  tone={avgSamples <= SAMPLE_WARNING_THRESHOLD ? 'warn' : 'ok'}
                />
              </div>
              <div className="rounded border border-border p-3">
                <div className="text-dim uppercase tracking-[1.5px] text-[10px]">
                  Status distribution
                </div>
                <div className="mt-2 space-y-1.5">
                  {severityBars.map((bar) => (
                    <div key={bar.label} className="grid gap-1.5">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <StatusGlow severity={bar.level as 0 | 1 | 2 | 3}>{bar.label}</StatusGlow>
                          <span>
                            {formatPercent(bar.count, severityDist.total)} of selected targets
                          </span>
                        </span>
                        <span>{bar.count}</span>
                      </div>
                      <div className="h-1.5 w-full bg-surface2 rounded">
                        <div
                          className={`h-full rounded ${bar.tone}`}
                          style={{ width: `${(bar.count / maxBarCount) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded border border-border p-3 bg-surface/30">
                <p className="text-muted">
                  Method: probes run from Cloudflare's worldwide edge/CDN footprint every 5 minutes,
                  not from one fixed server. Results aggregate in Analytics Engine by region/target
                  using the 15-minute window (`quantileExactWeighted` p50/p95), then cache to D1 for
                  trend analysis.
                </p>
                <p className="mt-2 text-muted">
                  D1 aggregate check: {latestHistorySummary}. If all rows show one sample with p50 =
                  p95, then current values are likely single-run snapshot fallback data rather than
                  a stable history window.
                </p>
                <p className="mt-2 text-muted">
                  <span className="text-bad font-semibold">Shortcomings:</span> synthetic probes
                  validate transport-layer behavior, not full page-rendering. They cannot see origin
                  app CPU saturation, cache misses behind CDN, or authenticated request paths that
                  are not represented by the test target.
                </p>
              </div>
              {severityNote && <p className="text-bad">⚠ {severityNote}</p>}
            </div>
          )}
        </CommandPanel>

        <CommandPanel>
          <SectionHeader
            title={
              <span className="flex items-center gap-2">
                <span>DATA QUALITY NOTES</span>
                <HelpTooltip content="Validation checks for probe aggregate quality before relying on historical interpretation." />
              </span>
            }
            expanded={metricsExpanded}
            onToggle={() => setMetricsExpanded((v) => !v)}
            contentId={metricsId}
            right={
              <span className="text-dim normal-case tracking-normal font-normal">
                {qualityWarning ? 'review recommended' : 'healthy'}
              </span>
            }
          />
          {metricsExpanded && (
            <div id={metricsId} className="px-3 py-3 text-xs leading-relaxed">
              <p className="text-muted">
                D1 historical logging is the source of truth for trend continuity. If you see only
                one-sample values or
                <code className="mx-1">p50 == p95</code> across every target, it usually means the
                cached probe snapshot is being displayed instead of aggregate history.
              </p>
              <div className="mt-3 grid md:grid-cols-2 gap-2">
                <div className="border border-border rounded px-2 py-2">
                  <div className="text-dim mb-1">Latest D1 sample depth</div>
                  <div>
                    {d1HealthLatestSnapshotTargets}
                    {' targets in latest snapshot · '}
                    {d1HealthLatestTargetsMultiSample}
                    {' multi-sample · '}
                    {d1HistorySampleRange}
                  </div>
                </div>
                <div className="border border-border rounded px-2 py-2">
                  <div className="text-dim mb-1">D1 aggregate quality</div>
                  <div>{latestHistorySummary}</div>
                </div>
                <div className="border border-border rounded px-2 py-2">
                  <div className="text-dim mb-1">D1 coverage</div>
                  <div>
                    {historyCoverageRate} · {historyP95SpreadRate} with spread{' '}
                    <span className="text-dim">(p95 &gt; p50)</span>
                  </div>
                </div>
                <div className="border border-border rounded px-2 py-2">
                  <div className="text-dim mb-1">Sample quality</div>
                  <div>
                    {multiSampleRate} with &gt;1 sample · {parityRate} exact parity
                  </div>
                </div>
                <div className="border border-border rounded px-2 py-2">
                  <div className="text-dim mb-1">Trend points in range</div>
                  <div>
                    {historicalTrendCount} snapshots loaded for {selected}
                  </div>
                </div>
                <div className="border border-border rounded px-2 py-2">
                  <div className="text-dim mb-1">Verification target</div>
                  <div>
                    Compare <span className="text-accent">/latency/recent</span> and{' '}
                    <span className="text-accent">
                      /latency/history?region={selected}&amp;hours={historyHours}
                    </span>
                  </div>
                </div>
              </div>
              {qualityWarning ? <p className="mt-2 text-bad">{qualityWarning}</p> : null}
              <p className="mt-2">
                If you want a hard verification, compare:
                <span className="text-dim ml-1">/latency/recent</span> for live per-region summaries
                and
                <span className="text-dim ml-1">/latency/history</span> for D1 snapshots.
              </p>
            </div>
          )}
        </CommandPanel>

        <CommandPanel>
          <SectionHeader
            title={
              <span className="flex items-center gap-2">
                <span>SOURCE HEALTH</span>
                <HelpTooltip content="Inspect whether the row data is coming from D1 aggregates or cache snapshot fallback." />
              </span>
            }
            right={
              <span className="text-dim normal-case tracking-normal font-normal">
                source: {sourceHealth.sourceMode}
              </span>
            }
            expanded={pipelineExpanded}
            onToggle={() => setPipelineExpanded((v) => !v)}
            contentId={pipelineId}
          />
          {pipelineExpanded && (
            <div className="px-3 py-3">
              <MetricTile
                label="selected region history"
                value={`${sourceHealth.selectedHistoryRows} / ${sourceHealth.selectedRows} rows`}
                details={`${sourceHealth.selectedHistoryTargets} history targets · ${sourceHealth.selectedRecentTargets} live targets`}
                tone={sourceModeTone}
              />
              <div className="mt-2 grid md:grid-cols-2 gap-2 text-xs">
                <div className="border border-border rounded px-2 py-2">
                  <div className="text-dim mb-1">History freshness</div>
                  <div>
                    {sourceHealth.latestHistoryTs
                      ? `latest ${new Date(sourceHealth.latestHistoryTs).toLocaleTimeString()}`
                      : 'no historical snapshot yet'}
                  </div>
                </div>
                <div className="border border-border rounded px-2 py-2">
                  <div className="text-dim mb-1">Probe sample health</div>
                  <div>
                    {sourceHealth.multiSampleRows}/{sourceHealth.selectedRows} multi-sample rows ·{' '}
                    {sourceHealth.parityRows} equal p50/p95
                  </div>
                </div>
              </div>
              {sourceHealth.historyStale ? (
                <p className="mt-2 text-bad">
                  History stale:{' '}
                  {sourceHealth.historyAgeMinutes === null
                    ? 'n/a'
                    : `${sourceHealth.historyAgeMinutes.toFixed(1)}m`}
                  . Verify `/latency/history` cron and AE query.
                </p>
              ) : null}
              <p className="mt-2 text-xs text-muted">
                If all selected-region rows are one-sample probes ({sourceHealth.snapshotLikeRows}),
                D1 replacement should begin after the scheduled aggregate job writes fresh rows.
              </p>
            </div>
          )}
        </CommandPanel>

        <div className="grid lg:grid-cols-[280px_1fr] gap-4">
          <CommandPanel>
            <SectionHeader
              title={
                <span className="flex items-center gap-2">
                  <span>REGION SELECTOR</span>
                  <HelpTooltip content="Select one region to inspect both live and historical probes. Each region panel shows target count and latest health." />
                </span>
              }
              expanded={regionExpanded}
              onToggle={() => setRegionExpanded((v) => !v)}
              contentId={regionId}
              right={
                <span className="text-dim normal-case tracking-normal font-normal">
                  {targets.length} targets
                </span>
              }
            />
            {regionExpanded && (
              <div id={regionId} className="grid gap-2 p-2">
                {regions.length === 0 && (
                  <div className="text-dim text-xs px-2 py-3">loading regions…</div>
                )}
                {regionSummaries.map((summary) => {
                  const selectedClass =
                    selected === summary.region
                      ? 'bg-accent/15 text-accent border-accent'
                      : 'hover:bg-surface2';
                  return (
                    <button
                      type="button"
                      key={summary.region}
                      onClick={() => setSelectedRegion(summary.region)}
                      className={`text-left border border-border rounded px-2 py-2 text-sm ${selectedClass} transition-colors`}
                    >
                      <div className="font-mono text-white flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1">
                          {summary.region}
                          <HelpTooltip
                            content={`${summary.region} is ${describeRegion(summary.region)}. ${summary.targets} targets | avg p50 ${formatMs(
                              summary.avgP50,
                            )} | avg p95 ${formatMs(summary.avgP95)}`}
                          />
                        </span>
                        <StatusGlow severity={summary.severity}>
                          {LATENCY_STATUS_LABELS[summary.severity]}
                        </StatusGlow>
                      </div>
                      <div className="text-dim text-[11px] mt-1">
                        {summary.targets} targets · {formatSamples(summary.samples)} samples total
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CommandPanel>

          <CommandPanel>
            <SectionHeader
              title={
                <span className="flex items-center gap-2">
                  <span>SELECTED REGION TABLE: {selected}</span>
                  <HelpTooltip content="Rows show target-level aggregates and per-window trend deltas for the selected region." />
                </span>
              }
              right={
                <label className="text-xs text-dim flex items-center gap-2">
                  window
                  <select
                    value={historyHours}
                    onChange={(event) => setHistoryHours(event.target.value)}
                    className="bg-surface border border-border px-1 py-1 rounded text-text"
                  >
                    {DEFAULT_WINDOWS.map((h) => (
                      <option key={h} value={h}>
                        {HISTORY_TITLES[h] ?? `${h}h`}
                      </option>
                    ))}
                  </select>
                  <span className="text-dim">
                    {majorCount > 0 ? `MAJ ${majorCount}` : `${targets.length} targets`}
                  </span>
                </label>
              }
              expanded={tableExpanded}
              onToggle={() => setTableExpanded((v) => !v)}
              contentId={tableId}
            />
            {tableExpanded && (
              <div id={tableId} className="overflow-x-auto">
                <table className="w-full min-w-[1040px] text-xs text-left table-fixed">
                  <colgroup>
                    <col className="w-[48%]" />
                    <col className="w-[6ch]" />
                    <col className="w-[8ch]" />
                    <col className="w-[8ch]" />
                    <col className="w-[9ch]" />
                    <col className="w-[12ch]" />
                    <col className="w-[8ch]" />
                  </colgroup>
                  <thead className="text-dim uppercase tracking-[1.5px]">
                    <tr>
                      <th className="sticky top-0 bg-surface px-2 py-2">
                        <span className="flex items-center gap-1">
                          Target
                          <HelpTooltip
                            title="Target"
                            content="Probe URL slug or endpoint represented by this row."
                          />
                        </span>
                      </th>
                      <th className="sticky top-0 bg-surface px-2 py-2">
                        <span className="flex items-center gap-1">
                          Samples
                          <HelpTooltip
                            title="Samples"
                            content="Successful samples from the aggregate used by this row."
                          />
                        </span>
                      </th>
                      <th className="sticky top-0 bg-surface px-2 py-2">
                        <span className="flex items-center gap-1">
                          P50
                          <HelpTooltip
                            title="P50"
                            content="Median one-way latency for this target across this aggregate."
                          />
                        </span>
                      </th>
                      <th className="sticky top-0 bg-surface px-2 py-2">
                        <span className="flex items-center gap-1">
                          P95
                          <HelpTooltip
                            title="P95"
                            content="95th percentile latency, useful for detecting congestion tails and tail jitter."
                          />
                        </span>
                      </th>
                      <th className="sticky top-0 bg-surface px-2 py-2">
                        <span className="flex items-center gap-1">
                          Trend
                          <HelpTooltip
                            title="Trend"
                            content="Delta between the newest and previous snapshot for this target in the selected window."
                          />
                        </span>
                      </th>
                      <th className="sticky top-0 bg-surface px-2 py-2">
                        <span className="flex items-center gap-1">
                          p95 Trend
                          <HelpTooltip
                            title="p95 Trend"
                            content="A sparkline of recent p95 latency values for this target, up to the last 10 points."
                          />
                        </span>
                      </th>
                      <th className="sticky top-0 bg-surface px-2 py-2">
                        <span className="flex items-center gap-1">
                          Status
                          <HelpTooltip
                            title="Status"
                            content="Severity is based on p95 latency only: OK below 300ms, DEG at 300ms+, MAJ at 900ms+, DOWN at 1500ms+. Sample depth is shown separately."
                          />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {targetColumns.map((target) => {
                      const row = tableRows.find((r) => r.target === target);
                      const trendClass =
                        row?.trendDirection === 'up'
                          ? 'text-bad'
                          : row?.trendDirection === 'down'
                            ? 'text-ok'
                            : 'text-dim';
                      return (
                        <tr
                          key={target}
                          className={`border-t border-border/50 ${row?.row ? 'hover:bg-surface2' : ''} ${severityToneClass(
                            row?.severity ?? 'missing',
                          )}`}
                        >
                          <td className="px-2 py-2 font-mono text-text truncate" title={target}>
                            {target}
                          </td>
                          <td className="px-2 py-2 font-mono text-right tabular-nums">
                            {row?.samples ? formatSamples(row.samples) : '—'}
                          </td>
                          <td className="px-2 py-2 font-mono text-white">
                            {row?.row ? formatMs(row.row.p50_ms) : '—'}
                          </td>
                          <td className="px-2 py-2 font-mono text-white">
                            {row?.row ? formatMs(row.row.p95_ms) : '—'}
                          </td>
                          <td className={`px-2 py-2 font-mono ${trendClass}`}>
                            {row?.trendDirection === 'up'
                              ? `↗ +${formatMs(row.trendMs)}`
                              : row?.trendDirection === 'down'
                                ? `↘ -${formatMs(row.trendMs)}`
                                : '—'}
                          </td>
                          <td className="px-2 py-2">
                            <LatencySparkline values={historySeries(regionHistoryRows, target)} />
                          </td>
                          <td className="px-2 py-2">
                            <StatusGlow severity={row?.severity ?? 'missing'}>
                              {severityLabel(row?.severity ?? 'missing')}
                            </StatusGlow>
                          </td>
                        </tr>
                      );
                    })}
                    {tableRows.length === 0 && (
                      <tr>
                        <td className="px-2 py-6 text-center text-dim" colSpan={7}>
                          no data for {selected}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CommandPanel>
        </div>

        <CommandPanel>
          <SectionHeader
            title={
              <span className="flex items-center gap-2">
                <span>REGIONAL TREND CHART</span>
                <HelpTooltip
                  content="Horizontal axis is time. Vertical axis is latency in milliseconds. This chart averages the selected region's target set from Cloudflare edge probes."
                  title="Trend chart"
                />
              </span>
            }
            expanded={chartExpanded}
            onToggle={() => setChartExpanded((v) => !v)}
            contentId={chartId}
            right={
              <span className="text-dim normal-case tracking-normal font-normal">
                {HISTORY_TITLES[historyHours] ?? `${historyHours}h`}
              </span>
            }
          />
          {chartExpanded && (
            <div id={chartId} className="p-3">
              <LatencyTrendChart
                rows={regionHistoryRows}
                targets={targetColumns}
                title={`${selected} target-set trend`}
                subtitle="Time runs left to right; latency in ms auto-scales vertically for the selected window."
              />
            </div>
          )}
        </CommandPanel>

        <CommandPanel>
          <SectionHeader
            title={
              <span className="flex items-center gap-2">
                <span>ALL REGIONS P95 TREND</span>
                <HelpTooltip
                  content="Compares p95 latency across all Cloudflare edge regions. Horizontal axis is time; vertical axis is latency in milliseconds and auto-scales to the visible data."
                  title="All-region trend"
                />
              </span>
            }
            expanded={allRegionsChartExpanded}
            onToggle={() => setAllRegionsChartExpanded((v) => !v)}
            contentId={allRegionsChartId}
            right={
              <span className="text-dim normal-case tracking-normal font-normal">
                {HISTORY_TITLES[historyHours] ?? `${historyHours}h`}
              </span>
            }
          />
          {allRegionsChartExpanded && (
            <div id={allRegionsChartId} className="p-3">
              <RegionLatencyComparisonChart
                rows={allHistoryRows}
                regions={REGION_DISPLAY_ORDER}
                title="Cloudflare edge region comparison"
                subtitle="Each line is a Cloudflare global edge region; p95 latency is averaged across visible probe targets."
              />
            </div>
          )}
        </CommandPanel>

        <CommandPanel>
          <SectionHeader
            title={
              <span className="flex items-center gap-2">
                <span>TARGET LATENCY PROFILE</span>
                <HelpTooltip
                  content="Snapshot comparison of current p50/p95 by target, sorted by current p95."
                  title="Target profile"
                />
              </span>
            }
            expanded={rankingExpanded}
            onToggle={() => setRankingExpanded((v) => !v)}
            contentId={rankingId}
            right={
              <span className="text-dim normal-case tracking-normal font-normal">
                top {topTargetRows.length}
              </span>
            }
          />
          {rankingExpanded && (
            <div id={rankingId} className="px-3 py-3 text-xs">
              {topTargetRows.length === 0 ? (
                <div className="rounded border border-border px-3 py-3 text-dim">
                  no profile data yet
                </div>
              ) : (
                <div className="space-y-2">
                  {topTargetRows.map((row) => {
                    const p50Width =
                      topBarP95Max > 0 ? `${(row.p50_ms / topBarP95Max) * 100}%` : '0%';
                    const p95Width =
                      topBarP95Max > 0 ? `${(row.p95_ms / topBarP95Max) * 100}%` : '0%';
                    return (
                      <div key={row.target} className="grid gap-1">
                        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                          <span className="font-mono text-white break-all">{row.target}</span>
                          <span className="text-dim">
                            {formatSamples(row.samples)} · {formatMs(row.p95_ms)}
                          </span>
                        </div>
                        <div className="rounded border border-border overflow-hidden bg-surface2 h-3 relative">
                          <div
                            className={`absolute inset-y-0 left-0 ${latencyProfileBarClass(row.tone, 'p95')}`}
                            style={{ width: p95Width }}
                          />
                          <div
                            className={`absolute inset-y-0 left-0 ${latencyProfileBarClass(row.tone, 'p50')}`}
                            style={{ width: p50Width }}
                          />
                        </div>
                        <div className="text-dim flex justify-between text-[10px]">
                          <span>
                            p50{' '}
                            <StatusGlow severity={row.tone}>{severityLabel(row.tone)}</StatusGlow>
                          </span>
                          <span>
                            {formatMs(row.p50_ms)} / {formatMs(row.p95_ms)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </CommandPanel>

        <CommandPanel>
          <SectionHeader
            title={
              <span className="flex items-center gap-2">
                <span>PROBE METHODOLOGY</span>
                <HelpTooltip content="Short reference for how the latency engine works and when interpretations can fail." />
              </span>
            }
            expanded={methodExpanded}
            onToggle={() => setMethodExpanded((v) => !v)}
            contentId={methodId}
          />
          {methodExpanded && (
            <div id={methodId} className="px-3 py-3 space-y-3 text-xs leading-relaxed text-muted">
              <p>
                <span className="text-accent font-semibold">How it is built:</span> each Cloudflare
                edge region worker runs the same target set in parallel and records one probe metric
                per target every 5 minutes. These measurements represent Cloudflare's worldwide
                CDN/edge view of the internet, not a single server or origin location.
              </p>
              <p>
                <span className="text-accent font-semibold">How we summarize:</span> every 15
                minutes, the API aggregates by region+target with weighted quantiles
                (`quantileExactWeighted`) to produce p50/p95 and sample counts. The newest aggregate
                is shown here for each selected region.
              </p>
              <p>
                <span className="text-accent font-semibold">Known limitations:</span> one synthetic
                request represents one point in time. Low-traffic / sparse windows can look flat or
                under-sampled. Auth-only pages, CDN edge variance, and login-aware pages will never
                fully mirror end-user experience.
              </p>
              <p>
                <span className="text-accent font-semibold">Verification target:</span> D1 should
                show multiple samples per target in stable windows, with p50 often lower than p95.
                If every row stays at one sample and exact parity, verify the cron schedule and AE
                query permissions.
              </p>
            </div>
          )}
        </CommandPanel>
      </main>
    </div>
  );
}
